import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import Lozenge from "@atlaskit/lozenge";
import { token } from "@atlaskit/tokens";

import { Picture } from "@/components/wpl/image/src/image";
import { usePricingContentContext } from "@/components/wpl/pricing/src/pricing-content-context";
import { Button } from "@/libraries/design/components";
import { Row } from "@/libraries/wpl/foundations/src/grid";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";

import { usePremiumSignupUrl } from "./urls.ts";

// eslint-disable-next-line complexity
export const LoomAiSection = () => {
    const context = usePricingContentContext();
    const loomAiContent = context?.datacenterPlans?.[0];

    const ctaContent =
        loomAiContent?.microCta?.[0]?.__typename ===
        "ContentPlatformCallToActionMicrocopy"
            ? loomAiContent.microCta[0]
            : undefined;

    const textParagraph = loomAiContent?.planOneLiner ?? "";
    const textBullets = loomAiContent?.highlightedFeaturesContainer?.map(
        (highlightedFeature) => {
            return {
                text: highlightedFeature?.highlightedFeatureTitleExternal,
                callOut: highlightedFeature?.callOut
            };
        }
    );

    const zeroMarginCss = css({ margin: 0 });

    const defaultPremiumSignupUrl = usePremiumSignupUrl();

    const intl = useIntl();

    return (
        <TypographyStyles>
            <Row layout="6-6" alignItems="center">
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/07v5f6mik43gk4jga0367a2x5qn64vbp.webp",
                        alt: intl.formatMessage({
                            id: "LoomPricingPage.LoomAiSection.meetingGraphicAltText",
                            defaultMessage:
                                "Example graphic of using Loom AI for meetings"
                        })
                    }}
                    style={{ width: "100%", height: "auto" }}
                />
                <div>
                    <span
                        css={css({
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "flex-start",
                            gap: token("space.050")
                        })}
                    >
                        <h2 css={zeroMarginCss}>
                            {loomAiContent?.planTitleExternal}
                        </h2>
                        <Picture
                            image={{
                                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/y42547423ap63hd11cr1832n3n455pt5.webp",
                                alt: ""
                            }}
                            style={{
                                width: "100%",
                                height: "27px",
                                position: "relative",
                                insetBlockStart: token("space.050")
                            }}
                        />
                    </span>
                    <p css={zeroMarginCss}>{textParagraph}</p>
                    <ul css={css({ marginBlockEnd: token("space.200") })}>
                        {textBullets?.map((bullet) => {
                            return (
                                <p css={zeroMarginCss} key={bullet.text}>
                                    <li>
                                        {bullet.text}
                                        {bullet.callOut && (
                                            <span
                                                css={css({
                                                    marginInlineStart:
                                                        token("space.050")
                                                })}
                                            >
                                                <Lozenge>
                                                    {bullet.callOut}
                                                </Lozenge>
                                            </span>
                                        )}
                                    </li>
                                </p>
                            );
                        })}
                    </ul>
                    <Button
                        href={ctaContent?.ctaUrl || defaultPremiumSignupUrl}
                        size={"small"}
                    >
                        {ctaContent?.ctaButtonText ||
                            intl.formatMessage({
                                id: "LoomAiSection.ctaButtonText",
                                defaultMessage: "Try for free"
                            })}
                    </Button>
                </div>
            </Row>
        </TypographyStyles>
    );
};
